// src/components/pages/Interviews.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import storageService from '../../services/storageService';
import { INTERVIEW_STATUS } from '../../constants/statusConstants';
import './Interviews.css';

const Interviews = () => {
  const [interviews, setInterviews] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  const [formData, setFormData] = useState({
    placementId: '',
    interviewDate: '',
    interviewTime: '',
    interviewType: 'Technical',
    interviewer: '',
    status: INTERVIEW_STATUS.SCHEDULED,
    notes: '',
    location: '',
    duration: '60'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    const allInterviews = storageService.getInterviews();
    const allPlacements = storageService.getPlacements();
    const allJobs = storageService.getJobs();
    const allCandidates = storageService.getCandidates();
    const allCompanies = storageService.getCompanies();
    
    const interviewsWithDetails = allInterviews.map(interview => {
      const placement = allPlacements.find(p => p.id === interview.placementId);
      const job = placement ? allJobs.find(j => j.id === placement.jobId) : null;
      const candidate = placement ? allCandidates.find(c => c.id === placement.candidateId) : null;
      const company = job ? allCompanies.find(c => c.id === job.companyId) : null;
      
      return {
        ...interview,
        candidateName: candidate?.name,
        jobTitle: job?.title,
        companyName: company?.name,
        candidateEmail: candidate?.email,
        placementStatus: placement?.status
      };
    }).filter(i => i.candidateName);
    
    // Filter active placements for dropdown
    const activePlacements = allPlacements.filter(p => 
      p.status !== 'Joined' && p.status !== 'Rejected'
    ).map(placement => {
      const job = allJobs.find(j => j.id === placement.jobId);
      const candidate = allCandidates.find(c => c.id === placement.candidateId);
      const company = job ? allCompanies.find(c => c.id === job.companyId) : null;
      
      return {
        ...placement,
        label: `${candidate?.name} - ${job?.title} (${company?.name})`
      };
    }).filter(p => p.label);
    
    setInterviews(interviewsWithDetails);
    setPlacements(activePlacements);
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    storageService.createInterview(formData);
    
    resetForm();
    loadData();
    setShowModal(false);
  };

  const handleStatusChange = (interviewId, newStatus) => {
    storageService.updateInterview(interviewId, { status: newStatus });
    loadData();
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this interview?')) {
      storageService.deleteInterview(id);
      loadData();
    }
  };

  const resetForm = () => {
    setFormData({
      placementId: '',
      interviewDate: '',
      interviewTime: '',
      interviewType: 'Technical',
      interviewer: '',
      status: INTERVIEW_STATUS.SCHEDULED,
      notes: '',
      location: '',
      duration: '60'
    });
  };

  // Filter interviews
  const filteredInterviews = interviews.filter(interview => {
    const matchesSearch = 
      interview.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      interview.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      interview.interviewer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      interview.companyName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || interview.status === filterStatus;
    const matchesType = filterType === 'all' || interview.interviewType === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case INTERVIEW_STATUS.SCHEDULED: return 'status-scheduled';
      case INTERVIEW_STATUS.COMPLETED: return 'status-completed';
      case INTERVIEW_STATUS.CANCELLED: return 'status-cancelled';
      case INTERVIEW_STATUS.NO_SHOW: return 'status-noshow';
      default: return 'status-default';
    }
  };

  const getStatusStats = () => {
    const stats = {};
    interviews.forEach(i => {
      stats[i.status] = (stats[i.status] || 0) + 1;
    });
    return stats;
  };

  const statusStats = getStatusStats();
  
  const today = new Date().toISOString().split('T')[0];
  const upcomingInterviews = interviews.filter(i => i.interviewDate >= today && i.status === INTERVIEW_STATUS.SCHEDULED).length;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading interviews...</p>
      </div>
    );
  }

  return (
    <div className="interviews-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Interview Scheduling</h1>
          <p>Manage and schedule candidate interviews</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <span className="btn-icon">+</span> Schedule Interview
        </button>
      </div>

      {/* Stats */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3>{interviews.length}</h3>
            <p>Total Interviews</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <h3>{upcomingInterviews}</h3>
            <p>Upcoming</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{statusStats[INTERVIEW_STATUS.COMPLETED] || 0}</h3>
            <p>Completed</p>
          </div>
        </div>
      </div>

      {/* Status Stats */}
      <div className="status-stats">
        {Object.values(INTERVIEW_STATUS).map(status => (
          <div key={status} className="status-stat-card">
            <div className={`status-indicator ${getStatusColor(status)}`}></div>
            <div className="status-stat-content">
              <h3>{statusStats[status] || 0}</h3>
              <p>{status}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search interviews by candidate, job, or interviewer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
        
        <div className="filter-controls">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            {Object.values(INTERVIEW_STATUS).map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="Technical">Technical</option>
            <option value="HR">HR</option>
            <option value="Managerial">Managerial</option>
            <option value="Cultural Fit">Cultural Fit</option>
          </select>
        </div>
      </div>

      {/* Interviews Table */}
      <div className="table-container">
        {filteredInterviews.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Job & Company</th>
                <th>Interview Details</th>
                <th>Interviewer</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInterviews.map(interview => (
                <tr key={interview.id}>
                  <td>
                    <div className="candidate-cell">
                      <div className="candidate-avatar">
                        {interview.candidateName.charAt(0)}
                      </div>
                      <div className="candidate-info">
                        <span className="candidate-name">{interview.candidateName}</span>
                        <span className="candidate-email">{interview.candidateEmail}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="job-cell">
                      <span className="job-title">{interview.jobTitle}</span>
                      <span className="company-name">{interview.companyName}</span>
                    </div>
                  </td>
                  <td>
                    <div className="interview-details">
                      <div className="interview-date">
                        <span className="detail-label">Date:</span>
                        <span className="detail-value">{interview.interviewDate}</span>
                      </div>
                      <div className="interview-time">
                        <span className="detail-label">Time:</span>
                        <span className="detail-value">{interview.interviewTime}</span>
                      </div>
                      <div className="interview-type">
                        <span className="type-badge">{interview.interviewType}</span>
                      </div>
                    </div>
                  </td>
                  <td>{interview.interviewer || 'Not specified'}</td>
                  <td>
                    <select
                      value={interview.status}
                      onChange={(e) => handleStatusChange(interview.id, e.target.value)}
                      className={`status-select ${getStatusColor(interview.status)}`}
                    >
                      {Object.values(INTERVIEW_STATUS).map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="notes-cell">
                      {interview.notes || 'No notes'}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => {
                          // Edit functionality
                          setFormData(interview);
                          setShowModal(true);
                        }}
                        className="action-btn edit"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={() => handleDelete(interview.id)} 
                        className="action-btn delete"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🎯</div>
            <h3>No interviews found</h3>
            <p>Try scheduling a new interview or adjust your search filters</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">
              Schedule First Interview
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{formData.id ? 'Edit Interview' : 'Schedule Interview'}</h2>
              <button onClick={() => { resetForm(); setShowModal(false); }} className="modal-close">
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Select Application *</label>
                    <select
                      name="placementId"
                      value={formData.placementId}
                      onChange={handleInputChange}
                      required
                      className="form-select"
                    >
                      <option value="">Select Candidate & Job</option>
                      {placements.map(placement => (
                        <option key={placement.id} value={placement.id}>
                          {placement.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Interview Date *</label>
                    <input
                      type="date"
                      name="interviewDate"
                      value={formData.interviewDate}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Interview Time *</label>
                    <input
                      type="time"
                      name="interviewTime"
                      value={formData.interviewTime}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Interview Type *</label>
                    <select
                      name="interviewType"
                      value={formData.interviewType}
                      onChange={handleInputChange}
                      required
                      className="form-select"
                    >
                      <option value="Technical">Technical</option>
                      <option value="HR">HR</option>
                      <option value="Managerial">Managerial</option>
                      <option value="Cultural Fit">Cultural Fit</option>
                      <option value="Final">Final Round</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Duration (minutes)</label>
                    <select
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                      <option value="90">90 minutes</option>
                      <option value="120">120 minutes</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Interviewer *</label>
                    <input
                      type="text"
                      name="interviewer"
                      value={formData.interviewer}
                      onChange={handleInputChange}
                      placeholder="Interviewer name"
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Location / Platform</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="e.g., Google Meet, Office Conference Room"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      {Object.values(INTERVIEW_STATUS).map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Interview Agenda & Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Interview agenda, topics to cover, special instructions..."
                    className="form-textarea"
                  />
                </div>
              </div>
              
              <div className="modal-footer">
                <button type="submit" className="btn-primary">
                  {formData.id ? 'Update Interview' : 'Schedule Interview'}
                </button>
                <button 
                  type="button" 
                  onClick={() => { resetForm(); setShowModal(false); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Interviews;